import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getFeaturedCategoryPath } from "../../../../helper/utils";
import ProductCard from "../../../common/productCard";
import "./style.css";

const CARD_WIDTH = 220;
const CARD_GAP = 16;
const VISIBLE_COUNT = 4;

function SingleRowWithArrows({ category, halfWidth = false }) {
  const scrollRef = useRef(null);
  const products = category.products || [];
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(products.length > (halfWidth ? 2 : VISIBLE_COUNT));

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atStart = el.scrollLeft <= 2;
    const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 2;
    setCanScrollLeft(!atStart);
    setCanScrollRight(!atEnd);
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = (CARD_WIDTH + CARD_GAP) * 2;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
    setTimeout(updateArrows, 300);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (el) {
      const ro = new ResizeObserver(updateArrows);
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, [products.length]);

  const blockClass = halfWidth
    ? "fcl-category-block fcl-category-block--single-row fcl-category-block--half"
    : "fcl-category-block fcl-category-block--single-row";

  const description = category.landingDescription || category.description || "";

  if (products.length === 0) {
    return (
      <article className={blockClass}>
        <div className="fcl-category-header">
          <div className="fcl-category-heading">
            <h3 className="fcl-category-title">{category.name}</h3>
            {description ? <p className="fcl-category-description">{description}</p> : null}
          </div>
          {category.pageUrl && (
            <Link to={getFeaturedCategoryPath(category.pageUrl)} className="fcl-view-all">View more</Link>
          )}
        </div>
        <p className="fcl-empty">No products in this category yet.</p>
      </article>
    );
  }

  return (
    <article className={blockClass}>
      <div className="fcl-category-header">
        <div className="fcl-category-heading">
          <h3 className="fcl-category-title">{category.name}</h3>
          {description ? <p className="fcl-category-description">{description}</p> : null}
        </div>
        {category.pageUrl && (
          <Link to={getFeaturedCategoryPath(category.pageUrl)} className="fcl-view-all">View more</Link>
        )}
      </div>
      <div className="fcl-single-row-wrap">
        {canScrollLeft && (
          <button
            type="button"
            className="fcl-arrow fcl-arrow--left"
            onClick={() => scroll(-1)}
            aria-label="Previous products"
          >
            ‹
          </button>
        )}
        <div
          ref={scrollRef}
          className={"fcl-product-list fcl-product-list--single-row" + (halfWidth ? " fcl-product-list--half" : "")}
          onScroll={updateArrows}
        >
          {products.map((product, index) => (
            <div key={product._id || product.sku || index} className="fcl-product-item fcl-product-item--single-row">
              <ProductCard
                product={product}
                isFromFeaturedCategory={true}
                isSelectable={product?.moq === 1}
              />
            </div>
          ))}
        </div>
        {canScrollRight && (
          <button
            type="button"
            className="fcl-arrow fcl-arrow--right"
            onClick={() => scroll(1)}
            aria-label="Next products"
          >
            ›
          </button>
        )}
      </div>
    </article>
  );
}

function CategoryBlock({ category, inRow, displayType }) {
  const products = category.products || [];
  const isCarousel = displayType === 3 || (category.catalogLayout || "grid-2x2") === "carousel";

  const blockClass =
    displayType === 2
      ? "fcl-category-block fcl-category-block--priority2"
      : displayType === 3
        ? "fcl-category-block fcl-category-block--carousel"
        : displayType === 1
          ? "fcl-category-block fcl-category-block--grid-default"
          : "fcl-category-block";

  const listClass =
    displayType === 2
      ? "fcl-product-list fcl-product-list--priority2"
      : displayType === 3
        ? "fcl-product-list fcl-product-list--carousel"
        : displayType === 1
          ? "fcl-product-list fcl-product-list--grid-5"
          : isCarousel
            ? "fcl-product-list fcl-product-list--carousel"
            : "fcl-product-list fcl-product-list--grid";

  const itemClass =
    displayType === 2
      ? "fcl-product-item fcl-product-item--priority2"
      : displayType === 3
        ? "fcl-product-item fcl-product-item--carousel"
        : displayType === 1
          ? "fcl-product-item fcl-product-item--grid"
          : isCarousel
            ? "fcl-product-item fcl-product-item--carousel"
            : "fcl-product-item";

  const description = category.landingDescription || category.description || "";

  return (
    <article className={blockClass + (inRow ? " fcl-category-block--in-row" : "")}>
      <div className="fcl-category-header">
        <div className="fcl-category-heading">
          <h3 className="fcl-category-title">{category.name}</h3>
          {description ? <p className="fcl-category-description">{description}</p> : null}
        </div>
        {category.pageUrl && (
          <Link to={getFeaturedCategoryPath(category.pageUrl)} className="fcl-view-all">View more</Link>
        )}
      </div>
      <div className={listClass}>
        {products.length > 0 ? (
          products.map((product, index) => (
            <div key={product._id || product.sku || index} className={itemClass}>
              <ProductCard
                product={product}
                isFromFeaturedCategory={true}
                isSelectable={product?.moq === 1}
              />
            </div>
          ))
        ) : (
          <p className="fcl-empty">No products in this category yet.</p>
        )}
      </div>
    </article>
  );
}

export default function FeaturedCategoriesLanding({ categories = [] }) {
  if (!categories.length) return null;

  // Build sections in landing page order (respects admin landingPageOrder / priority)
  const sections = [];
  for (let i = 0; i < categories.length; i++) {
    const cat = categories[i];
    const priority = Number(cat.landingLayoutPriority) || 1;
    if (priority === 2) {
      const next = categories[i + 1];
      if (next && (Number(next.landingLayoutPriority) || 1) === 2) {
        sections.push({ type: "twoSided", pair: [cat, next] });
        i += 1;
        continue;
      }
    }
    if (priority === 1) sections.push({ type: "grid", category: cat });
    else if (priority === 2) sections.push({ type: "singleRow", category: cat });
    else sections.push({ type: "singleRow", category: cat });
  }

  return (
    <section className="fcl-section">
      <div className="fcl-container fcl-container--full">
        <div className="fcl-categories">
          {sections.map((section, idx) => {
            if (section.type === "grid") {
              return (
                <div key={section.category.fcId || section.category.name || idx} className="fcl-section-block">
                  <div className="fcl-row fcl-row--full">
                    <CategoryBlock category={section.category} inRow={false} displayType={1} />
                  </div>
                </div>
              );
            }
            if (section.type === "twoSided") {
              const [left, right] = section.pair;
              return (
                <div key={`2s-${idx}`} className="fcl-section-block">
                  <div className="fcl-row fcl-row--left-right fcl-row--50-50">
                    <div className="fcl-col fcl-col--left">
                      {left ? <SingleRowWithArrows category={left} halfWidth /> : <div className="fcl-placeholder" />}
                    </div>
                    <div className="fcl-col fcl-col--right">
                      {right ? <SingleRowWithArrows category={right} halfWidth /> : <div className="fcl-placeholder" />}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <div key={section.category.fcId || section.category.name || idx} className="fcl-section-block">
                <div className="fcl-row fcl-row--full">
                  <SingleRowWithArrows category={section.category} halfWidth={false} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}